export type CancellationReason =
  | "too_expensive"
  | "dashboard_wont_load"
  | "script_didnt_install"
  | "found_a_bug"
  | "couldnt_run_ab_test"
  | "no_data_in_reports"
  | "missing_features"
  | "other";

export type RecoveryOffer = "discount" | "call";

export type CancelFlowMode = "cancel" | "delete";

export type CancelSurface =
  | "user_settings"
  | "hub_plan_card"
  | "user_settings_delete";

export type CancelOutcome =
  | "retained_discount"
  | "retained_call"
  | "handed_off";

export const CANCELLATION_REASONS: {
  value: CancellationReason;
  label: string;
}[] = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "dashboard_wont_load", label: "Dashboard doesn't load" },
  { value: "script_didnt_install", label: "Tracking script didn't install" },
  { value: "found_a_bug", label: "I hit a bug" },
  { value: "couldnt_run_ab_test", label: "Couldn't run an A/B test" },
  {
    value: "no_data_in_reports",
    label: "Heatmaps, funnels, or traffic were empty",
  },
  { value: "missing_features", label: "Missing a feature I need" },
  { value: "other", label: "Something else" },
];

/**
 * Retention offer shown to "too expensive" cancels. The customer copies the code
 * and applies it themselves in the Stripe billing portal; nothing is applied
 * server-side. PLACEHOLDER: create a matching promotion code in Stripe (Products →
 * Coupons → Promotion codes) and enable "Allow customers to apply promotion codes"
 * in the customer portal settings before shipping.
 */
export const RETENTION_COUPON = {
  code: "STAY30",
  offer: "30% off your next 3 months",
} as const;

export function recoveryOfferFor(reason: CancellationReason): RecoveryOffer {
  return reason === "too_expensive" ? "discount" : "call";
}

export function formatCancellationReason(
  reason: CancellationReason | "",
  detail?: string,
): string {
  if (!reason) return "";
  const trimmed = detail?.trim();
  if (reason === "other") {
    return trimmed ? `Other: ${trimmed}` : "Other";
  }
  return CANCELLATION_REASONS.find((item) => item.value === reason)?.label ?? reason;
}

export function callOfferCopy(reason: CancellationReason | ""): {
  heading: string;
  caption: string;
} {
  switch (reason) {
    case "dashboard_wont_load":
      return {
        heading: "We'll get the dashboard loading again",
        caption: "A 30-minute call to find the hang and get you back into traffic, tests, and reports.",
      };
    case "script_didnt_install":
      return {
        heading: "We'll install tracking with you",
        caption: "A 30-minute call to get the script live on your builder and confirm the first pageviews.",
      };
    case "found_a_bug":
      return {
        heading: "We'll reproduce the bug with you",
        caption: "A 30-minute call to capture what's broken and get a fix in motion.",
      };
    case "couldnt_run_ab_test":
      return {
        heading: "We'll launch the first test together",
        caption: "A 30-minute call to pick a page, set up the experiment, and get it live.",
      };
    case "no_data_in_reports":
      return {
        heading: "We'll find why the reports are empty",
        caption: "A 30-minute call to check tracking, heatmaps, and funnels until data shows up.",
      };
    default:
      return {
        heading: "Book a 30-minute setup call",
        caption: "Tracking live the same day, plus a first test worth running. Amir hosts it.",
      };
  }
}

export type CancelUsageSnapshot = {
  heatmaps: number;
  funnels: number;
  tests: number;
  views: number;
};

export function formatCompactCount(n: number): string {
  if (n >= 1_000_000) {
    const millions = n / 1_000_000;
    const rounded = millions >= 10 ? Math.round(millions) : Number(millions.toFixed(1));
    return `${rounded}M`;
  }
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
  if (n >= 1_000) {
    const thousands = n / 1_000;
    const rounded = Number.isInteger(thousands) ? thousands : Number(thousands.toFixed(1));
    return `${rounded}K`;
  }
  return n.toLocaleString("en-US");
}

function joinAnd(parts: string[]): string {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

function countedNoun(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Loss-aversion sentence for cancel step 2. Returns null when there's
 * nothing concrete to name — callers skip the line instead of inventing zeros.
 */
export function formatCancelLossCopy(snapshot: CancelUsageSnapshot): string | null {
  const parts: string[] = [];
  if (snapshot.heatmaps > 0) {
    parts.push(countedNoun(snapshot.heatmaps, "heatmap", "heatmaps"));
  }
  if (snapshot.funnels > 0) {
    parts.push(countedNoun(snapshot.funnels, "funnel", "funnels"));
  }
  if (snapshot.views > 0) {
    parts.push(`your ${formatCompactCount(snapshot.views)} view data`);
  }
  if (snapshot.tests > 0) {
    parts.push(
      snapshot.tests === 1
        ? "the A/B test you ran"
        : `the ${snapshot.tests} A/B tests you ran`,
    );
  }
  if (parts.length === 0) return null;
  return `You're going to lose ${joinAnd(parts)}!`;
}
